import { resolveOrganizationId } from '@/lib/routerUtils'
import type { OrganizationAction, OrganizationActionPayload } from '../../types/organization'
import { normalizeOrganizationDeviceActionsResponse } from '../actionsApi'
import type {
	ActionV3,
	ActionV3WithPayload,
	BulkMarkAsCompletedOptions,
	DeviceActionEntryV3,
	DeviceActionLogV3,
	FetchActionsForDeviceRequest,
	FetchActionsForDeviceResponse,
	FetchAllActionsV3Response,
	FetchDeviceActionsV3Request,
	FetchDeviceActionsV3Response,
	GetActionV3Options,
	GetDeviceActionLogsV3Options,
	GetDeviceActionLogsV3Response,
	GetDevicesForActionV3Request,
	GetDevicesForActionV3Response,
	LatencyHistogramV3Response,
	MarkDeviceActionsAsCompletedOptions,
	TriggerActionV3Payload,
	TriggerActionV3Response,
} from '../actionsApi'
import type { DeviceSearchFilters } from '../deviceApi'
import type { Filters } from '../deviceApi'
import { demoApi } from './db'

function normalizeOrgActionsResponse(data: unknown): { actions: OrganizationAction[]; count: number } {
	if (data == null || typeof data !== 'object') {
		return { actions: [], count: 0 }
	}
	const o = data as Record<string, unknown>
	const count = typeof o.count === 'number' ? o.count : Number(o.count) || 0
	const raw = o.actions ?? o.result
	const actions = (Array.isArray(raw) ? raw : []) as OrganizationAction[]
	return { actions, count }
}

async function demoFetchDeviceActionsV3(
	request: FetchDeviceActionsV3Request,
	_signal?: AbortSignal,
): Promise<FetchDeviceActionsV3Response> {
	// Server doesn't have a direct /device_actions endpoint
	// Fetch actions and aggregate device_actions
	const actionsRes = await demoApi.get<{ count: number; data: ActionV3[] }>(
		`/v1/actions?page=${request.page}&per_page=${request.per_page}`,
	)

	// Fetch devices for all actions in parallel to avoid N+1 sequential requests
	const actions = actionsRes.data || []
	const deviceResults = await Promise.all(
		actions.map(action =>
			demoApi
				.post<GetDevicesForActionV3Response>(`/v1/actions/${action.id}/devices`, {
					page: 1,
					per_page: 100,
				})
				.then(res => ({ action, devices: res.data || [] })),
		),
	)

	const deviceActionEntries: FetchDeviceActionsV3Response['data'] = deviceResults.flatMap(({ action, devices }) =>
		devices
			.filter(da => !request.device_id || da.device_id === request.device_id)
			.map(da => ({
				action_id: action.id,
				device_id: da.device_id,
				action_state: da.action_state,
				retry_count: da.retry_count,
				timeout_at: null,
				updated_at: action.created_at,
				device_reported_state: da.device_reported_state,
				progress: da.progress,
				errors: da.errors,
				mqtt_publish_at: null,
				payload: action.payload,
				type: action.type,
				created_at: action.created_at,
				created_by: action.created_by,
			})),
	)

	return {
		count: deviceActionEntries.length,
		data: deviceActionEntries,
	}
}

// Demo Actions API - forwards requests to Neon server (V3-compatible)
export const actionsApi = {
	fetchAllActionsStatus: async (
		pageNumber: number,
		pageLimit: number,
		sortByIncomplete = false,
		filters: Filters = {},
	): Promise<FetchAllActionsV3Response> => {
		void sortByIncomplete
		const params: Record<string, string | number> = {
			page: pageNumber,
			per_page: pageLimit,
		}

		if (filters.action_type) {
			params.action_type = Array.isArray(filters.action_type)
				? String(filters.action_type[0])
				: String(filters.action_type)
		}
		if (filters.email) {
			params.email = Array.isArray(filters.email) ? String(filters.email[0]) : String(filters.email)
		}

		const queryString = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
		return demoApi.get<FetchAllActionsV3Response>(`/v1/actions?${queryString}`)
	},

	/**
	 * Trigger an organization-level action
	 */
	triggerActionV3: async (
		payload: OrganizationActionPayload,
		options?: { tenantId?: string },
	): Promise<TriggerActionV3Response> => {
		// Detach runs through the org-level /reprovision endpoint
		if (payload.action_type === 'detach') {
			const { useOrganizationStore } = await import('@/stores/organizationStore')
			const { useProjectsStore } = await import('@/stores/projectsStore')
			const { organizationsApi: orgApi } = await import('./organizationsApi')
			const organizationId = resolveOrganizationId()
			const orgDevices = useOrganizationStore.getState().devices
			const rootProject = useProjectsStore.getState().allProjects.find(p => p.is_org)
			if (!rootProject) {
				throw new Error('No root project found for this organization; detach is not available.')
			}
			for (const d of payload.devices) {
				const od = orgDevices.find(x => x.tenant_id === d.tenant_id && String(x.id) === d.device_id)
				if (od?.uid == null || od.uid === '') {
					throw new Error(`Device ${d.device_id} has no UID; detach requires an org device with UID.`)
				}
				const uid = typeof od.uid === 'number' ? od.uid : Number(od.uid)
				if (!Number.isFinite(uid)) {
					throw new Error(`Device ${d.device_id} has an invalid UID.`)
				}
				await orgApi.detachDeviceViaReprovision(organizationId, uid, rootProject.id, payload.params)
			}
			return { id: 0 }
		}

		// Normalize union payload variants to V3 action shape.
		const body =
			payload.action_type === 'attach'
				? {
						action_type: payload.action_type,
						payload: payload.params as unknown as Record<string, unknown>,
						device_filter: { id: { in: payload.device_ids } },
					}
				: {
						action_type: payload.action_type,
						payload: payload.payload as unknown as Record<string, unknown>,
						device_filter: payload.device_filter,
					}

		const postOptions = options?.tenantId
			? {
					headers: {
						'x-bytebeam-tenant': options.tenantId,
						'x-bytebeam-impersonate': 'admin',
					},
				}
			: undefined

		const response = await demoApi.post<{ id: number; status?: string }>(
			'/v1/actions',
			body,
			postOptions as Record<string, unknown>,
		)
		return { id: response.id }
	},

	/**
	 * Get organization-level actions
	 */
	getOrganizationActions: async (
		organizationId: string,
		page = 1,
		perPage = 20,
		tenant?: string,
		actionType = 'renew_cert',
	): Promise<{ actions: OrganizationAction[]; count: number }> => {
		const q = new URLSearchParams({ page: String(page), per_page: String(perPage) })
		// Org-level views surface only certificate-renewal/reprovision actions, stored under the internal `renew_cert` type.
		q.set('action_type', actionType)
		if (tenant) {
			q.set('tenant', tenant)
		}
		const data = await demoApi.getNoTenant<unknown>(
			`/organizations/${encodeURIComponent(organizationId)}/actions?${q.toString()}`,
		)
		return normalizeOrgActionsResponse(data)
	},

	getDeviceActions: async (
		organizationId: string,
		uid: number,
		page = 1,
		perPage = 20,
		tenant?: string,
		_signal?: AbortSignal,
	): Promise<{ actions: DeviceActionEntryV3[]; count: number }> => {
		const q = new URLSearchParams({ page: String(page), per_page: String(perPage) })
		// Org-level views surface only certificate-renewal/reprovision actions, stored under the internal `renew_cert` type.
		q.set('action_type', 'renew_cert')
		if (tenant) {
			q.set('tenant', tenant)
		}
		const data = await demoApi.getNoTenant<unknown>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/actions?${q.toString()}`,
		)
		return normalizeOrganizationDeviceActionsResponse(data, String(uid))
	},

	// Fetches fleet-wide device-actions for an org (demo).
	fetchOrgDeviceActionsV3: async (
		organizationId: string,
		request: FetchDeviceActionsV3Request & { action_type?: string; tenant?: string },
		_signal?: AbortSignal,
	): Promise<{ count: number; action_counts?: Record<string, number>; data: DeviceActionEntryV3[] }> => {
		const q = new URLSearchParams({ page: String(request.page), per_page: String(request.per_page) })
		if (request.action_type) {
			q.set('action_type', request.action_type)
		}
		if (request.action_state) {
			q.set('action_state', request.action_state)
		}
		if (request.tenant) {
			q.set('tenant', request.tenant)
		}
		const data = await demoApi.getNoTenant<unknown>(
			`/organizations/${encodeURIComponent(organizationId)}/device_actions?${q.toString()}`,
		)
		const o = (data ?? {}) as Record<string, unknown>
		const countRaw = o.count
		const count = typeof countRaw === 'number' ? countRaw : Number(countRaw) || 0
		const action_counts =
			o.action_counts && typeof o.action_counts === 'object' ? (o.action_counts as Record<string, number>) : undefined
		const { actions } = normalizeOrganizationDeviceActionsResponse(data)
		return { count, action_counts, data: actions }
	},

	/**
	 * Get devices for a specific action with their status
	 * Server returns V3 format: { count, data: DeviceActionV3[] }
	 */
	getDevicesForActionV3: async (
		actionId: number,
		request: GetDevicesForActionV3Request,
	): Promise<GetDevicesForActionV3Response> => {
		return demoApi.post<GetDevicesForActionV3Response>(`/v1/actions/${actionId}/devices`, request)
	},

	/**
	 * Fetch device-level action records with full details
	 * Note: Demo server has limited filtering compared to production V3
	 */
	fetchDeviceActionsV3: demoFetchDeviceActionsV3,

	/**
	 * Same response as tenant `GET /v3/devices/{device_id}/actions`; demo delegates to aggregated device_actions.
	 */
	fetchActionsForDevice: async (
		request: FetchActionsForDeviceRequest,
		_signal?: AbortSignal,
	): Promise<FetchActionsForDeviceResponse> =>
		demoFetchDeviceActionsV3({
			page: request.page,
			per_page: request.per_page,
			device_id: request.device_id,
			action_type: request.action_type,
			creator: request.email,
		}),

	/**
	 * Get action logs for a specific device
	 * Demo backend returns a bare array; wrap into the paginated `{ count, data }` shape.
	 */
	getDeviceActionLogsV3: async (
		actionId: number,
		deviceId: string,
		_options?: GetDeviceActionLogsV3Options,
	): Promise<GetDeviceActionLogsV3Response> => {
		const data = await demoApi.get<DeviceActionLogV3[]>(
			`/v1/actions/${actionId}/devices/${encodeURIComponent(deviceId)}/logs`,
		)
		return { count: data.length, data }
	},

	/**
	 * Get a single action with its payload
	 * Server returns V3 format: ActionV3WithPayload
	 */
	getActionV3: async (actionId: number, _options?: GetActionV3Options): Promise<ActionV3WithPayload> => {
		return demoApi.get<ActionV3WithPayload>(`/v1/actions/${actionId}`)
	},

	/**
	 * Mark action as completed for specific devices
	 * tenantId/actionStatus are ignored (demo backend doesn't support them).
	 */
	markActionAsCompletedV3: async (
		actionId: number,
		deviceFilter?: DeviceSearchFilters,
		_tenantId?: string,
		_actionStatus?: string,
	): Promise<void> => {
		await demoApi.post(`/v1/actions/${actionId}/mark-as-completed`, {
			device_filter: deviceFilter,
		})
	},

	/**
	 * Mark active action for a specific device as completed
	 */
	markDeviceActionAsCompletedV3: async (
		deviceId: string,
		_tenantId?: string,
		_actionStatus?: string,
	): Promise<void> => {
		await demoApi.post('/v1/actions/mark-as-completed', {
			device_filter: { id: { in: [deviceId] } },
		})
	},

	/**
	 * Bulk mark active actions as completed for multiple devices
	 */
	bulkMarkAsCompletedV3: async ({ deviceFilter }: BulkMarkAsCompletedOptions): Promise<void> => {
		await demoApi.post('/v1/actions/mark-as-completed', {
			device_filter: deviceFilter,
		})
	},

	/**
	 * Mark specific (action_id, device_id) pairs as completed in a single request
	 */
	markDeviceActionsAsCompletedV3: async ({ deviceActions }: MarkDeviceActionsAsCompletedOptions): Promise<void> => {
		// Demo has no filter-only form; without pairs there is nothing to target.
		if (!deviceActions?.length) {
			return
		}
		await demoApi.post('/v1/actions/mark-as-completed', {
			device_filter: { id: { in: deviceActions.map(da => da.device_id) } },
		})
	},

	/**
	 * Get latency histogram for an action
	 * Note: Server endpoint not yet implemented - returns empty histogram
	 */
	getLatencyHistogramV3: async (
		actionId: number,
		actionState: string,
		bins = 25,
	): Promise<LatencyHistogramV3Response> => {
		// TODO: Implement latency histogram endpoint on server
		console.warn('getLatencyHistogramV3: Server endpoint not implemented for demo')
		await demoApi.post(`/v1/actions/${actionId}/device-latencies`, {
			state: actionState,
			buckets_count: bins,
		})
		return { count: 0, bins: [] }
	},

	/**
	 * Create/Trigger an action
	 * Server accepts V3 payload directly
	 */
	createActionV3: async (payload: TriggerActionV3Payload, tenantId?: string): Promise<TriggerActionV3Response> => {
		const options = tenantId
			? {
					headers: {
						'x-bytebeam-tenant': tenantId,
						'x-bytebeam-impersonate': 'admin',
					},
				}
			: undefined

		const response = await demoApi.post<{ id: number; status?: string }>(
			'/v1/actions',
			payload,
			options as Record<string, unknown>,
		)
		return { id: response.id }
	},
}
